<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl capitalize">
          {{ $t('Quantities.SurveyedQuantityModal.title', { code: wbsCode.code }) }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="w-full overflow-y-auto h-full">
        <div class="flex">
          <form
            v-if="projectsStore.project?.unarchived"
            class="flex flex-col space-y-2 w-full"
            @submit.prevent="saveSurveyed"
          >
            <h3 class="text-center text-xl">
              {{ $t('Quantities.SurveyedQuantityModal.recordNewSurvey') }}
            </h3>

            <label :title="$t('Quantities.SurveyedQuantityModal.surveyDateTooltip')">
              {{ $t('Quantities.SurveyedQuantityModal.surveyDate') }}

              <AppInput
                v-model="surveyedQuantityForm.form.day"
                type="date"
                name="survey_day"
                data-test="survey_day"
                class="form-input"
                :error="surveyedQuantityForm.getErrors('day')"
              />
            </label>

            <label :title="$t('Quantities.SurveyedQuantityModal.surveyedQuantityTooltip')">
              {{ $t('Quantities.SurveyedQuantityModal.surveyedQuantity', { unit: wbsCode.unit }) }}

              <AppInput
                v-model.number="surveyedQuantityForm.form.quantity"
                type="number"
                name="survey_quantity"
                data-test="survey_quantity"
                class="form-input"
                :error="surveyedQuantityForm.getErrors('quantity')"
              />
            </label>

            <button
              type="submit"
              :disabled="createSurveyAction.state.value === States.LOADING ||
                updateSurveyAction.state.value === States.LOADING"
              class="btn btn--primary-blue"
              data-test="save_surveyed"
            >
              {{ $t('Quantities.SurveyedQuantityModal.button.saveSurveyedQuantity') }}
            </button>
          </form>

          <div
            v-if="projectsStore.project?.unarchived"
            class="border-gray-300 w-1 border-l translate-x-1/2 mx-2"
          />

          <div class="flex flex-col space-y-2 w-full">
            <h3 class="text-center text-xl">
              {{ $t('Quantities.SurveyedQuantityModal.pastSurveys') }}
            </h3>

            <div class="flex justify-center w-full space-x-4">
              <ul
                v-if="surveyedQuantities.length > 0"
                class="overflow-auto space-y-4 h-64 flex-1"
              >
                <li
                  v-for="quantity in surveyedQuantities"
                  :key="quantity.id"
                  class="card__list-item"
                >
                  <div class="grid grid-cols-2">
                    <div class="flex flex-col w-max">
                      <p>
                        {{ new Date(quantity.day).toLocaleDateString() }}
                      </p>

                      <AppInput
                        v-if="updateSurvey"
                        v-model.number="updateSurveyedQuantityForm.form.quantity"
                        type="text"
                        :error="updateSurveyedQuantityForm.getErrors('quantity')"
                        name="quantity"
                        class="form-input w-32"
                        @blur="updateSurveyed"
                        @keyup.enter="updateSurveyed"
                        @keyup.esc="updateSurvey = false"
                      />

                      <p v-else>
                        {{ quantity.quantity }}
                      </p>
                    </div>

                    <div class="flex justify-end row-span-2 items-center">
                      <button
                        v-if="quantity.id === latestSurvey?.id"
                        type="button"
                        data-test="edit"
                        class="mr-2"
                        @click="selectSurvey(quantity)"
                      >
                        <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="btn-icon h-5 w-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
                      </button>
                    </div>
                  </div>
                </li>
              </ul>

              <p
                v-if="surveyedQuantities.length === 0"
                class="text-center font-semibold"
              >
                {{ $t('Quantities.SurveyedQuantityModal.noSurveysRecorded') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close');"
        >
          {{ $t('Quantities.SurveyedQuantityModal.button.close') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import SurveyedQuantity from '@/models/SurveyedQuantity';
import WbsCode from '@/models/WbsCode';
import { useProjectsStore } from '@/store/projects';
import { useSurveyedQuantitiesStore } from '@/store/surveyedQuantities';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';

const props = defineProps<{
  wbsCode: WbsCode;
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'refresh');
}>();

const projectsStore = useProjectsStore();
const surveyedQuantitiesStore = useSurveyedQuantitiesStore();

const surveyedQuantityForm = useFormData({
  day: '',
  quantity: 0,
  wbs_code_id: props.wbsCode.id,
});

const updateSurveyedQuantityForm = useFormData({
  quantity: 0,
});

const surveyedQuantities = computed(() => {
  return surveyedQuantitiesStore.models
    .where('project_id', projectsStore.project?.id)
    .where('wbs_code_id', props.wbsCode.id)
    .orderBy('day', 'desc')
    .get();
});

const latestSurvey = computed(() => {
  return surveyedQuantities.value[0];
});

const createSurveyAction = useStoreApiAction(surveyedQuantitiesStore.createQuantity);
const updateSurveyAction = useStoreApiAction(surveyedQuantitiesStore.updateQuantity);

const saveSurveyed = () => {
  surveyedQuantityForm.resetErrors();

  createSurveyAction.request(projectsStore.project?.id, surveyedQuantityForm.form).then(() => {
    Swal.fire({
      icon: 'success',
      text: t('Quantities.SurveyedQuantityModal.savedSurveyedQuantity'),
    });

    surveyedQuantityForm.resetData();

    emits('refresh');
    emits('close');
  }).catch((error) => {
    surveyedQuantityForm.setErrors(error.data);
  });
};

const updateSurveyed = () => {
  updateSurveyedQuantityForm.resetErrors();

  updateSurveyAction.request(selectedSurveyId.value, updateSurveyedQuantityForm.form).then(() => {
    updateSurvey.value = false;

    Swal.fire({
      icon: 'success',
      text: t('Quantities.SurveyedQuantityModal.updatedSurveyedQuantity'),
    });

    updateSurveyedQuantityForm.resetData();
  }).catch((error) => {
    updateSurveyedQuantityForm.setErrors(error.data);
  });
};

const selectedSurveyId = ref();
const updateSurvey = ref(false);

const selectSurvey = (survey: SurveyedQuantity) => {
  selectedSurveyId.value = survey.id;
  updateSurvey.value = true;
  updateSurveyedQuantityForm.form.quantity = survey.quantity;
};
</script>

<style scoped></style>
